import React, { useState, useEffect } from 'react';
import {
    Card,
    Text,
    Box,
    BlockStack,
    InlineStack,
    Spinner,
    Banner,
    Button,
    FormLayout,
    TextField,
    Divider,
} from '@shopify/polaris';
import {
    PlusIcon,
    DeleteIcon,
} from '@shopify/polaris-icons';
import apiClient from '../utils/api';
import { validateNumeric } from '../utils/inputSecurity';
export const InvoiceSettings = ({
    newRecipientEmail,
    setNewRecipientEmail,
    brandId,
    selectedTab
}) => {
    const [invoiceSettingsLoading, setInvoiceSettingsLoading] = useState(false);
    const [invoiceSettingsError, setInvoiceSettingsError] = useState('');
    const [invoiceSettings, setInvoiceSettings] = useState({
        companyName: '',
        address: '',
        phone: '',
        registrationNumber: '',
        taxNumber: '',
        vatRate: 0,
        paymentMethod: '',
        recipientEmails: [],
    });
    const [invoiceSettingsSaving, setInvoiceSettingsSaving] = useState(false);
    const [invoiceSettingsSuccess, setInvoiceSettingsSuccess] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});

    // Fetch invoice settings when switching to settings tab
    useEffect(() => {
        const fetchInvoiceSettings = async () => {
            if (!brandId || selectedTab !== 0) return;

            setInvoiceSettingsLoading(true);
            setInvoiceSettingsError('');

            try {
                const response = await apiClient.getInvoiceSettings(brandId);
                if (response.success) {
                    setInvoiceSettings({
                        companyName: response.settings.invoiceCompanyName || '',
                        address: response.settings.invoiceAddress || '',
                        phone: response.settings.invoicePhone || '',
                        registrationNumber: response.settings.invoiceRegistrationNumber || '',
                        taxNumber: response.settings.invoiceTaxNumber || '',
                        vatRate: response.settings.invoiceVatRate || 0,
                        paymentMethod: response.settings.invoicePaymentMethod || '',
                        recipientEmails: response.settings.invoiceRecipientEmails || [],
                    });
                }
            } catch (err) {
                console.error('Error fetching invoice settings:', err);
                setInvoiceSettingsError('Failed to load invoice settings');
            } finally {
                setInvoiceSettingsLoading(false);
            }
        };

        fetchInvoiceSettings();
    }, [brandId, selectedTab]);

    if (invoiceSettingsLoading) {
        return (
            <Card>
                <Box padding="600">
                    <InlineStack align="center" blockAlign="center" gap="400">
                        <Spinner size="small" />
                        <Text>Loading invoice settings...</Text>
                    </InlineStack>
                </Box>
            </Card>
        );
    }

    // Invoice settings handlers
    const handleInvoiceSettingsChange = (field, value) => {
        setInvoiceSettings(prev => ({
            ...prev,
            [field]: value
        }));
        setFieldErrors(prev => ({ ...prev, [field]: undefined }));
        setInvoiceSettingsSuccess('');
    };

    // Phone validation helper
    const validatePhone = (phone) => {
        if (!phone || phone.trim() === '') {
            return { isValid: false, error: 'Phone number is required' };
        }
        const phoneRegex = /^[\d\s\-\(\)\+]+$/;
        if (!phoneRegex.test(phone)) {
            return { isValid: false, error: 'Invalid phone format' };
        }
        if (phone.length > 20) {
            return { isValid: false, error: 'Phone number too long' };
        }
        return { isValid: true, error: null };
    };

    // Form validation
    const validateForm = () => {
        const errors = {};

        // Company Name - Required
        if (!invoiceSettings.companyName?.trim()) {
            errors.companyName = 'Company name is required';
        } else if (invoiceSettings.companyName.length > 200) {
            errors.companyName = 'Company name must be less than 200 characters';
        }

        // Address Required
        if (!invoiceSettings.address?.trim()) {
            errors.address = 'Address is required';
        }

        // Phone - Validate format if provided
        const phoneResult = validatePhone(invoiceSettings.phone);
        if (!phoneResult.isValid) {
            errors.phone = phoneResult.error;
        }

        // Registration Number - Required
        if (!invoiceSettings.registrationNumber?.trim()) {
            errors.registrationNumber = 'Registration number is required';
        } else if (invoiceSettings.registrationNumber.length > 100) {
            errors.registrationNumber = 'Must be less than 100 characters';
        }

        // Tax Number - Max length
        if (invoiceSettings.taxNumber?.length > 100) {
            errors.taxNumber = 'Must be less than 100 characters';
        }

        // VAT Rate - Numeric 0-100
        const vatResult = validateNumeric(invoiceSettings.vatRate, { min: 0, max: 100 });
        if (!vatResult.isValid) {
            errors.vatRate = vatResult.error;
        }

        // Payment Method - Required
        if (!invoiceSettings.paymentMethod?.trim()) {
            errors.paymentMethod = 'Payment method is required';
        } else if (invoiceSettings.paymentMethod.length > 200) {
            errors.paymentMethod = 'Must be less than 200 characters';
        }

        // Recipient Emails - At least one required
        if (!invoiceSettings.recipientEmails || invoiceSettings.recipientEmails.length === 0) {
            errors.recipientEmails = 'At least one recipient email is required';
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleAddRecipientEmail = () => {
        const email = newRecipientEmail.trim();
        if (!email) return;

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setInvoiceSettingsError('Please enter a valid email address');
            return;
        }

        if (invoiceSettings.recipientEmails.includes(email)) {
            setInvoiceSettingsError('This email is already in the list');
            return;
        }

        setInvoiceSettings(prev => ({
            ...prev,
            recipientEmails: [...prev.recipientEmails, email]
        }));
        setNewRecipientEmail('');
        setInvoiceSettingsError('');
        setInvoiceSettingsSuccess('');
        setFieldErrors(prev => ({ ...prev, recipientEmails: undefined }));
    };

    const handleRemoveRecipientEmail = (emailToRemove) => {
        setInvoiceSettings(prev => ({
            ...prev,
            recipientEmails: prev.recipientEmails.filter(email => email !== emailToRemove)
        }));
        setInvoiceSettingsSuccess('');
    };

    const handleSaveInvoiceSettings = async () => {
        if (!validateForm()) return;

        setInvoiceSettingsSaving(true);
        setInvoiceSettingsError('');
        setInvoiceSettingsSuccess('');

        try {
            const response = await apiClient.updateInvoiceSettings(brandId, invoiceSettings);
            if (response.success) {
                setInvoiceSettingsSuccess('Invoice settings saved successfully');
            } else {
                setInvoiceSettingsError(response.error || 'Failed to save invoice settings');
            }
        } catch (err) {
            console.error('Error saving invoice settings:', err);
            setInvoiceSettingsError(err.message || 'Failed to save invoice settings');
        } finally {
            setInvoiceSettingsSaving(false);
        }
    };

    return (
        <Card>
            {/* Header with Save Button */}
            <Box
                padding="400"
                background="bg-surface"
                borderBlockEndWidth="025"
                borderColor="border"
                position="sticky"
                zIndex='21'
                insetBlockStart="0"
            >
                <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingLg" as="h2">Invoice Settings</Text>
                    <Button
                        variant="primary"
                        onClick={handleSaveInvoiceSettings}
                        loading={invoiceSettingsSaving}
                    >
                        Save Settings
                    </Button>
                </InlineStack>
            </Box>

            <Box padding="600">
                <BlockStack gap="600">
                    {/* Error/Success Banners */}
                    {invoiceSettingsError && (
                        <Banner tone="critical" onDismiss={() => setInvoiceSettingsError('')}>
                            {invoiceSettingsError}
                        </Banner>
                    )}
                    {invoiceSettingsSuccess && (
                        <Banner tone="success" onDismiss={() => setInvoiceSettingsSuccess('')}>
                            {invoiceSettingsSuccess}
                        </Banner>
                    )}

                    {/* Company & Payment Information */}
                    <FormLayout>
                        <TextField
                            label="Company Name"
                            value={invoiceSettings.companyName}
                            onChange={(value) => handleInvoiceSettingsChange('companyName', value)}
                            error={fieldErrors.companyName}
                        />

                        <TextField
                            label="Company Address"
                            value={invoiceSettings.address}
                            onChange={(value) => handleInvoiceSettingsChange('address', value)}
                            multiline={3}
                            helpText="Enter the complete company address"
                            error={fieldErrors.address}
                        />

                        <TextField
                            label="Phone Number"
                            value={invoiceSettings.phone}
                            onChange={(value) => handleInvoiceSettingsChange('phone', value)}
                            type="tel"
                            error={fieldErrors.phone}
                        />

                        <TextField
                            label="Company Registration Number"
                            value={invoiceSettings.registrationNumber}
                            onChange={(value) => handleInvoiceSettingsChange('registrationNumber', value)}
                            error={fieldErrors.registrationNumber}
                        />

                        <TextField
                            label="Tax Number"
                            value={invoiceSettings.taxNumber}
                            onChange={(value) => handleInvoiceSettingsChange('taxNumber', value)}
                            helpText="Optional - for international brands"
                        />

                        <TextField
                            label="VAT Rate (%)"
                            type="number"
                            value={invoiceSettings.vatRate?.toString() || '0'}
                            onChange={(value) => {
                                const numValue = parseFloat(value) || 0;
                                const clampedValue = Math.min(100, Math.max(0, numValue));
                                handleInvoiceSettingsChange('vatRate', clampedValue);
                            }}
                            helpText="VAT percentage (0-100). Set to 0 for no VAT."
                            min={0}
                            max={100}
                            suffix="%"
                            error={fieldErrors.vatRate}
                        />

                        <TextField
                            label="Payment Method"
                            value={invoiceSettings.paymentMethod}
                            onChange={(value) => handleInvoiceSettingsChange('paymentMethod', value)}
                            error={fieldErrors.paymentMethod}
                        />
                    </FormLayout>

                    <Divider />

                    {/* Email Recipients */}
                    <BlockStack gap="500">
                        <BlockStack gap="200">
                            <Text variant="headingMd" as="h3">Invoice Recipients</Text>
                            <Text variant="bodyMd" tone="subdued">
                                Email addresses that will receive invoice notifications
                            </Text>
                            {fieldErrors.recipientEmails && (
                                <Text variant="bodyMd" tone="critical">
                                    {fieldErrors.recipientEmails}
                                </Text>
                            )}
                        </BlockStack>

                        <InlineStack gap="300" blockAlign="end">
                            <Box minWidth="300px">
                                <TextField
                                    label="Add Recipient Email"
                                    value={newRecipientEmail}
                                    onChange={setNewRecipientEmail}
                                    type="email"
                                    placeholder="email@example.com"
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddRecipientEmail();
                                        }
                                    }}
                                />
                            </Box>
                            <Button
                                icon={PlusIcon}
                                onClick={handleAddRecipientEmail}
                                disabled={!newRecipientEmail.trim()}
                            >
                                Add
                            </Button>
                        </InlineStack>

                        {invoiceSettings.recipientEmails.length > 0 ? (
                            <BlockStack gap="200">
                                {invoiceSettings.recipientEmails.map((email, index) => (
                                    <InlineStack key={index} align="space-between" blockAlign="center">
                                        <Text>{email}</Text>
                                        <Button
                                            icon={DeleteIcon}
                                            variant="plain"
                                            tone="critical"
                                            onClick={() => handleRemoveRecipientEmail(email)}
                                            accessibilityLabel={`Remove ${email}`}
                                        />
                                    </InlineStack>
                                ))}
                            </BlockStack>
                        ) : (
                            <Text variant="bodyMd" tone="subdued">
                                No recipient emails configured. Add at least one email to receive invoices.
                            </Text>
                        )}
                    </BlockStack>
                </BlockStack>
            </Box>
        </Card>
    );
}